import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Shuffle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'

// Idea #80: click from different Yandex hosts by weighted chance.
// Stored format (read by the scenario parser parse_search_url_weights):
//   "url;weight|url;weight"
//   e.g. "https://ya.ru;60|https://yandex.ru;30|https://yandex.com.tr;10"

type Row = { url: string; weight: number }

const DEFAULT_ROWS: Row[] = [
  { url: 'https://ya.ru', weight: 60 },
  { url: 'https://yandex.ru', weight: 30 },
  { url: 'https://yandex.com.tr', weight: 10 },
]

function parseValue(raw: string): Row[] {
  if (!raw || !raw.trim()) return []
  const out: Row[] = []
  for (const entry of raw.split('|').map(s => s.trim()).filter(Boolean)) {
    let url = entry
    let weight = 1
    const semi = entry.indexOf(';')
    if (semi >= 0) {
      url = entry.slice(0, semi).trim()
      const wn = Number(entry.slice(semi + 1).trim())
      if (Number.isFinite(wn) && wn > 0) weight = wn
    }
    if (url) out.push({ url, weight })
  }
  return out
}

function serializeValue(rows: Row[]): string {
  return rows
    .map(r => ({ url: r.url.trim(), weight: r.weight }))
    .filter(r => r.url && r.weight > 0)
    .map(r => `${r.url};${r.weight}`)
    .join('|')
}

export function SearchUrlWeightsField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<Row[]>(parseValue(value))

  // Re-sync from external value whenever modal opens.
  useEffect(() => {
    if (open) {
      const parsed = parseValue(value)
      setRows(parsed.length ? parsed : DEFAULT_ROWS.map(r => ({ ...r })))
    }
  }, [open, value])

  const summary = useMemo(() => {
    const parsed = parseValue(value)
    if (!parsed.length) return ''
    const total = parsed.reduce((s, r) => s + (r.weight > 0 ? r.weight : 0), 0) || 1
    const labels = parsed.slice(0, 3).map(r => {
      const host = r.url.replace(/^https?:\/\//, '').replace(/\/$/, '')
      const pct = Math.round((r.weight / total) * 100)
      return `${host} ${pct}%`
    })
    const tail = parsed.length > 3 ? ` +${parsed.length - 3}` : ''
    return `${parsed.length} хостов · ${labels.join(', ')}${tail}`
  }, [value])

  const addRow = () => setRows(r => [...r, { url: '', weight: 0 }])
  const updateRow = (i: number, patch: Partial<Row>) =>
    setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))
  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i))

  const cleaned = useMemo(
    () => rows.filter(r => r.url.trim() && r.weight > 0),
    [rows]
  )
  const totalWeight = useMemo(
    () => cleaned.reduce((s, r) => s + r.weight, 0),
    [cleaned]
  )
  const sumOk = totalWeight === 100

  const save = () => {
    if (!sumOk) return
    onChange(serializeValue(cleaned))
    setOpen(false)
  }

  const clearAll = () => {
    onChange('')
    setRows(DEFAULT_ROWS.map(r => ({ ...r })))
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full max-w-80 justify-start gap-2 text-left"
        onClick={() => setOpen(true)}
      >
        <Shuffle className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span className="truncate text-xs">
          {summary || 'Задать случайный URL…'}
        </span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle>Случайный URL поиска</DialogTitle>
            <DialogDescription>
              Заходи на выдачу с разных хостов Яндекса по шансу. На каждой итерации
              хост выбирается случайно с учётом веса. Сумма шансов должна быть{' '}
              <strong>100%</strong>. Если список пуст — используется обычный «URL поиска».
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            {rows.length === 0 && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                URL не добавлены. Нажмите «Добавить URL» снизу.
              </div>
            )}
            <div className="flex flex-col gap-2">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-border/40 bg-muted/30 p-2"
                >
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      type="text"
                      value={row.url}
                      onChange={e => updateRow(i, { url: e.target.value })}
                      placeholder="https://ya.ru"
                      className="h-9 text-sm font-mono"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Шанс</span>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={row.weight === 0 ? '' : row.weight}
                      placeholder="0"
                      onChange={e => {
                        const raw = e.target.value
                        if (raw === '') { updateRow(i, { weight: 0 }); return }
                        let n = Number(raw)
                        if (Number.isFinite(n)) {
                          if (n < 0) n = 0
                          if (n > 100) n = 100
                          updateRow(i, { weight: n })
                        }
                      }}
                      className="w-20 h-9 text-sm tabular-nums"
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeRow(i)}
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Button type="button" variant="outline" size="sm" onClick={addRow} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Добавить URL
              </Button>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t flex flex-row items-center justify-between gap-2">
            <div className={`text-xs tabular-nums ${sumOk ? 'text-muted-foreground' : 'text-destructive'}`}>
              Сумма шансов: <span className="font-semibold">{totalWeight}%</span>
              {!sumOk && <> · должно быть 100%</>}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={clearAll}>
                Очистить
              </Button>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button type="button" onClick={save} disabled={!sumOk}>
                Сохранить
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
